// How a tested model compares with the model in use, in plain words.

import type { ModelCandidate } from "@/services/adminModelCurrency";

export type Verdict = "better" | "worse" | "same";

export interface Comparison {
  verdict: Verdict;
  /** The model the test compared against. */
  against: string;
  metric: string;
  candidate: string;
  current: string;
  scale: string;
}

type Side = Record<string, unknown>;

interface Metric {
  label: string;
  /** Number compared to decide better or worse. */
  value: (side: Side) => number | null;
  show: (side: Side) => string;
  scale: (side: Side) => string;
  /** Differences smaller than this read as "about the same" (judge scores wobble a little). */
  tolerance: number;
}

const num = (value: unknown): number | null =>
  typeof value === "number" && Number.isFinite(value) ? value : null;

const outOfFive = (field: string, label: string): Metric => ({
  label,
  value: side => num(side[field]),
  show: side => (num(side[field]) ?? 0).toFixed(2),
  scale: () => "out of 5",
  tolerance: 0.05,
});

/** Headline number per test kind; program-news tests are absolute and have no side-by-side. */
const METRICS: Record<string, Metric> = {
  chat: outOfFive("overallScore", "answer quality"),
  titleCall: outOfFive("relevanceMean", "title relevance"),
  coachEmail: outOfFive("overallMean", "email quality"),
  intentClassifier: {
    label: "right answers",
    value: side => num(side.correct),
    show: side => String(num(side.correct) ?? 0),
    scale: side => `of ${num(side.total) ?? "?"}`,
    tolerance: 0.5,
  },
};

const isSide = (value: unknown): value is Side => !!value && typeof value === "object";

export function compareWithCurrent(candidate: ModelCandidate): Comparison | null {
  const results = candidate.evalResults;
  const metric = results ? METRICS[results.kind] : undefined;
  const summary = results?.summary;
  if (!results || !metric || !isSide(summary)) return null;
  const { candidate: mine, incumbent: theirs } = summary as {
    candidate?: unknown;
    incumbent?: unknown;
  };
  if (!isSide(mine) || !isSide(theirs)) return null;
  const a = metric.value(mine);
  const b = metric.value(theirs);
  if (a === null || b === null) return null;
  const diff = a - b;
  const verdict: Verdict =
    Math.abs(diff) < metric.tolerance ? "same" : diff > 0 ? "better" : "worse";
  return {
    verdict,
    against: results.incumbentModel,
    metric: metric.label,
    candidate: metric.show(mine),
    current: metric.show(theirs),
    scale: metric.scale(mine),
  };
}

/** The test compared against a model that is no longer the one in use. */
export function isOutOfDate(candidate: ModelCandidate, currentModel: string): boolean {
  const against = candidate.evalResults?.incumbentModel;
  return !!against && against !== currentModel;
}
